import { useState } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdSearch } from "react-icons/md";
import {
  getVariationAmount,
  getVariationCode,
  getVariationName,
} from "./utils";

// Reusable searchable select for large option lists (e.g. vehicle makes/models).
const InsuranceSearchSelect = ({
  label,
  placeholder,
  selectPlaceholder,
  value,
  options,
  isDisabled = false,
  onChange,
}) => {
  const [filter, setFilter] = useState("");
  const filtered =
    filter.trim() === ""
      ? options
      : options.filter((opt) =>
          opt.name.toLowerCase().includes(filter.toLowerCase()),
        );

  return (
    <FormControl isDisabled={isDisabled}>
      <FormLabel>{label}</FormLabel>
      {!isDisabled && options.length > 10 && (
        <InputGroup size="sm" mb="6px">
          <InputLeftElement pointerEvents="none">
            <MdSearch />
          </InputLeftElement>
          <Input
            placeholder={placeholder}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            borderRadius="md"
          />
        </InputGroup>
      )}
      <Select
        placeholder={selectPlaceholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {filtered.map((opt) => (
          <option key={opt.code} value={opt.code}>
            {opt.name}
          </option>
        ))}
      </Select>
      {!isDisabled && filter.trim() !== "" && filtered.length === 0 && (
        <Text fontSize="12px" color="gray.500" mt="4px">
          No results for "{filter}"
        </Text>
      )}
    </FormControl>
  );
};

export const PayBillFormFields = ({
  item,
  formValues,
  handleInputChange,
  serviceIDs,
  electricityServiceIDs,
  tvServiceIDs,
  dataVariants,
  tvVariants,
  insuranceVariants,
  vehicleColors,
  engineCapacities,
  insuranceStates,
  insuranceLGAs,
  vehicleBrands,
  vehicleModels,
  educationVariants,
  jambProfileName,
}) => {
  if (item === "electricity") {
    return (
      <VStack spacing="12px" align="stretch">
        <FormControl>
          <FormLabel>Meter Number</FormLabel>
          <Input
            placeholder="Meter Number"
            value={formValues.electricity.meterNumber}
            onChange={(event) =>
              handleInputChange(
                "electricity",
                "meterNumber",
                event.target.value,
              )
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Service Provider</FormLabel>
          <Select
            placeholder="Select Service Provider"
            value={formValues.electricity.serviceProvider}
            onChange={(event) =>
              handleInputChange(
                "electricity",
                "serviceProvider",
                event.target.value,
              )
            }
          >
            {Object.entries(electricityServiceIDs || {}).map(
              ([label, value]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ),
            )}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Amount</FormLabel>
          <Input
            type="number"
            placeholder="Amount"
            value={formValues.electricity.amount}
            onChange={(event) =>
              handleInputChange("electricity", "amount", event.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Type</FormLabel>
          <Select
            placeholder="Select Type"
            value={formValues.electricity.type}
            onChange={(event) =>
              handleInputChange("electricity", "type", event.target.value)
            }
          >
            <option value="prepaid">Prepaid</option>
            <option value="postpaid">Postpaid</option>
          </Select>
        </FormControl>
      </VStack>
    );
  }

  if (item === "airtime") {
    return (
      <VStack spacing="12px" align="stretch">
        <FormControl>
          <FormLabel>Phone Number</FormLabel>
          <Input
            placeholder="Phone Number"
            value={formValues.airtime.phoneNumber}
            onChange={(event) =>
              handleInputChange("airtime", "phoneNumber", event.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Network</FormLabel>
          <Select
            placeholder="Select Network"
            value={formValues.airtime.network}
            onChange={(event) =>
              handleInputChange("airtime", "network", event.target.value)
            }
          >
            <option value="mtn">MTN</option>
            <option value="airtel">Airtel</option>
            <option value="glo">Glo</option>
            <option value="9mobile">9mobile</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Amount</FormLabel>
          <Input
            type="number"
            placeholder="Amount to Pay"
            value={formValues.airtime.amount}
            onChange={(event) =>
              handleInputChange("airtime", "amount", event.target.value)
            }
          />
        </FormControl>
      </VStack>
    );
  }

  if (item === "data") {
    return (
      <VStack spacing="12px" align="stretch">
        <FormControl>
          <FormLabel>Phone Number</FormLabel>
          <Input
            placeholder="Phone Number"
            value={formValues.data.phoneNumber}
            onChange={(event) =>
              handleInputChange("data", "phoneNumber", event.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Network</FormLabel>
          <Select
            placeholder="Select Network"
            value={formValues.data.network}
            onChange={(event) =>
              handleInputChange("data", "network", event.target.value)
            }
          >
            {Object.entries(serviceIDs || {}).map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Data Plan</FormLabel>
          <Select
            placeholder="Select Data Plan"
            value={formValues.data.dataPlan}
            onChange={(event) =>
              handleInputChange("data", "dataPlan", event.target.value)
            }
          >
            {dataVariants.map((variation) => {
              const code = getVariationCode(variation);
              const amount = getVariationAmount(variation);

              return (
                <option key={code} value={code}>
                  {getVariationName(variation)} - ₦{amount.toLocaleString()}
                </option>
              );
            })}
          </Select>
        </FormControl>
      </VStack>
    );
  }

  if (item === "insurance") {
    return (
      <VStack spacing="12px" align="stretch">
        <FormControl>
          <FormLabel>Insurance Plan</FormLabel>
          <Select
            placeholder="Select Insurance Plan"
            value={formValues.insurance.insurancePlan}
            onChange={(event) =>
              handleInputChange(
                "insurance",
                "insurancePlan",
                event.target.value,
              )
            }
          >
            {insuranceVariants.map((variation) => {
              const code = getVariationCode(variation);
              const amount = getVariationAmount(variation);
              return (
                <option key={code} value={code}>
                  {getVariationName(variation)} - ₦{amount.toLocaleString()}
                </option>
              );
            })}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Phone Number</FormLabel>
          <Input
            placeholder="Phone Number"
            value={formValues.insurance.phone}
            onChange={(event) =>
              handleInputChange("insurance", "phone", event.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            placeholder="Email Address"
            value={formValues.insurance.email}
            onChange={(event) =>
              handleInputChange("insurance", "email", event.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Insured Name</FormLabel>
          <Input
            placeholder="Full Name of Insured"
            value={formValues.insurance.insuredName}
            onChange={(event) =>
              handleInputChange("insurance", "insuredName", event.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Plate Number</FormLabel>
          <Input
            placeholder="e.g. ATU480ER"
            value={formValues.insurance.plateNumber}
            onChange={(event) =>
              handleInputChange("insurance", "plateNumber", event.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Chassis Number</FormLabel>
          <Input
            placeholder="Chassis / VIN Number"
            value={formValues.insurance.chassisNumber}
            onChange={(event) =>
              handleInputChange(
                "insurance",
                "chassisNumber",
                event.target.value,
              )
            }
          />
        </FormControl>
        <InsuranceSearchSelect
          label="Vehicle Make"
          placeholder="Search vehicle makes..."
          selectPlaceholder="Select Vehicle Make"
          value={formValues.insurance.vehicleMake}
          options={vehicleBrands}
          onChange={(value) =>
            handleInputChange("insurance", "vehicleMake", value)
          }
        />
        <InsuranceSearchSelect
          label="Vehicle Model"
          placeholder="Search vehicle models..."
          selectPlaceholder={
            formValues.insurance.vehicleMake
              ? "Select Vehicle Model"
              : "Select a make first"
          }
          value={formValues.insurance.vehicleModel}
          options={vehicleModels}
          isDisabled={!formValues.insurance.vehicleMake}
          onChange={(value) =>
            handleInputChange("insurance", "vehicleModel", value)
          }
        />
        <FormControl>
          <FormLabel>Vehicle Color</FormLabel>
          <Select
            placeholder="Select Vehicle Color"
            value={formValues.insurance.vehicleColor}
            onChange={(event) =>
              handleInputChange("insurance", "vehicleColor", event.target.value)
            }
          >
            {vehicleColors.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Engine Capacity</FormLabel>
          <Select
            placeholder="Select Engine Capacity"
            value={formValues.insurance.engineCapacity}
            onChange={(event) =>
              handleInputChange(
                "insurance",
                "engineCapacity",
                event.target.value,
              )
            }
          >
            {engineCapacities.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Year of Make</FormLabel>
          <Input
            type="number"
            placeholder="e.g. 2009"
            min="1900"
            max={new Date().getFullYear()}
            value={formValues.insurance.yearOfMake}
            onChange={(event) =>
              handleInputChange("insurance", "yearOfMake", event.target.value)
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>State</FormLabel>
          <Select
            placeholder="Select State"
            value={formValues.insurance.state}
            onChange={(event) =>
              handleInputChange("insurance", "state", event.target.value)
            }
          >
            {insuranceStates.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>LGA</FormLabel>
          <Select
            placeholder={
              formValues.insurance.state ? "Select LGA" : "Select a state first"
            }
            value={formValues.insurance.lga}
            isDisabled={!formValues.insurance.state}
            onChange={(event) =>
              handleInputChange("insurance", "lga", event.target.value)
            }
          >
            {insuranceLGAs.map((item) => (
              <option key={item.code} value={item.code}>
                {item.name}
              </option>
            ))}
          </Select>
        </FormControl>
      </VStack>
    );
  }

  if (item === "education") {
    const isJamb = formValues.education.educationService === "jamb";
    const isWaecReg =
      formValues.education.educationService === "waec-registration";
    return (
      <VStack spacing="12px" align="stretch">
        <FormControl>
          <FormLabel>Service</FormLabel>
          <Select
            placeholder="Select Education Service"
            value={formValues.education.educationService}
            onChange={(event) => {
              handleInputChange(
                "education",
                "educationService",
                event.target.value,
              );
              handleInputChange("education", "plan", "");
              handleInputChange("education", "quantity", "1");
              handleInputChange("education", "billersCode", "");
            }}
          >
            <option value="waec-registration">WAEC Registration PIN</option>
            <option value="waec">WAEC Result Checker PIN</option>
            <option value="jamb">JAMB</option>
          </Select>
        </FormControl>
        <FormControl isDisabled={!formValues.education.educationService}>
          <FormLabel>Plan</FormLabel>
          <Select
            placeholder={
              formValues.education.educationService
                ? "Select Plan"
                : "Select a service first"
            }
            value={formValues.education.plan}
            onChange={(event) =>
              handleInputChange("education", "plan", event.target.value)
            }
          >
            {educationVariants.map((variation) => {
              const code = getVariationCode(variation);
              const amount = getVariationAmount(variation);
              return (
                <option key={code} value={code}>
                  {getVariationName(variation)} - ₦{amount.toLocaleString()}
                </option>
              );
            })}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Phone Number</FormLabel>
          <Input
            placeholder="Phone Number"
            value={formValues.education.phone}
            onChange={(event) =>
              handleInputChange("education", "phone", event.target.value)
            }
          />
        </FormControl>
        {(isWaecReg || formValues.education.educationService === "waec") && (
          <FormControl>
            <FormLabel>Quantity</FormLabel>
            <Input
              type="number"
              min="1"
              placeholder="1"
              value={formValues.education.quantity}
              onChange={(event) =>
                handleInputChange("education", "quantity", event.target.value)
              }
            />
          </FormControl>
        )}
        {isJamb && (
          <FormControl>
            <FormLabel>JAMB Profile ID</FormLabel>
            <Input
              placeholder="Enter your JAMB Profile ID"
              value={formValues.education.billersCode}
              onChange={(event) =>
                handleInputChange(
                  "education",
                  "billersCode",
                  event.target.value,
                )
              }
            />
            {jambProfileName && (
              <Text fontSize="12px" color="green.600" fontWeight="600" mt="4px">
                ✓ Profile found: {jambProfileName}
              </Text>
            )}
          </FormControl>
        )}
      </VStack>
    );
  }

  return (
    <VStack spacing="12px" align="stretch">
      <FormControl>
        <FormLabel>Decoder Number</FormLabel>
        <Input
          placeholder="Enter Decoder Number"
          value={formValues.cable.decoderNumber}
          onChange={(event) =>
            handleInputChange("cable", "decoderNumber", event.target.value)
          }
        />
      </FormControl>
      <FormControl>
        <FormLabel>Cable TV</FormLabel>
        <Select
          placeholder="Select Cable TV"
          value={formValues.cable.cableTv}
          onChange={(event) =>
            handleInputChange("cable", "cableTv", event.target.value)
          }
        >
          {Object.entries(tvServiceIDs || {}).map(([label, value]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Cable Plan</FormLabel>
        <Select
          placeholder="Select Cable Plan"
          value={formValues.cable.cablePlan}
          onChange={(event) =>
            handleInputChange("cable", "cablePlan", event.target.value)
          }
        >
          {tvVariants.map((variation) => {
            const code = getVariationCode(variation);
            const amount = getVariationAmount(variation);

            return (
              <option key={code} value={code}>
                {getVariationName(variation)} - ₦{amount.toLocaleString()}
              </option>
            );
          })}
        </Select>
      </FormControl>
    </VStack>
  );
};
